import React, { useState } from 'react';
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Text,
  VStack,
  useDisclosure,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription
} from '@chakra-ui/react';
import { FiAlertTriangle, FiSkipForward } from 'react-icons/fi';

/**
 * Manual Phase Advance Button
 * Only use if automatic phase advancement fails
 * Logs usage and notifies support
 */

const PHASE_TRANSITIONS = {
  'DISPATCH': 'EN_ROUTE',
  'EN_ROUTE': 'ON_SCENE',
  'ON_SCENE': 'LOADED',
  'LOADED': 'TRANSPORTING',
  'TRANSPORTING': 'RADIO_REPORT',
  'RADIO_REPORT': 'END'
};

export default function ManualPhaseAdvanceButton({
  currentPhase,
  sessionId,
  scenarioId,
  turnCount,
  onAdvance,
  disabled = false
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isAdvancing, setIsAdvancing] = useState(false);
  const toast = useToast();

  const nextPhase = PHASE_TRANSITIONS[currentPhase];

  if (!nextPhase) {
    // No next phase (already at END or unknown phase)
    return null;
  }

  // Only show if at least 1 response has occurred in this phase
  if (turnCount < 1) {
    return null;
  }

  const handleAdvanceClick = async () => {
    setIsAdvancing(true);

    try {
      // Log manual advancement
      const logData = {
        sessionId,
        scenarioId,
        fromPhase: currentPhase,
        toPhase: nextPhase,
        turnCount,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        reason: 'Manual advancement by user - automatic transition did not occur'
      };

      // Call backend to log and send notifications
      const response = await fetch('/api/log-manual-phase-advance', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(logData)
      });

      if (!response.ok) {
        throw new Error('Failed to log manual advancement');
      }

      // Notify user
      toast({
        title: 'Phase Advanced',
        description: `Moved from ${currentPhase} to ${nextPhase}. Support has been notified.`,
        status: 'warning',
        duration: 5000,
        isClosable: true
      });

      // Call parent's advance handler
      if (onAdvance) {
        await onAdvance(nextPhase);
      }

      onClose();
    } catch (error) {
      console.error('Manual advancement failed:', error);
      toast({
        title: 'Advancement Failed',
        description: 'Could not advance phase. Please try again or contact support.',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsAdvancing(false);
    }
  };

  return (
    <>
      <Button
        leftIcon={<FiSkipForward />}
        size="sm"
        variant="outline"
        colorScheme="orange"
        onClick={onOpen}
        isDisabled={disabled}
      >
        Manual Advance
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Manual Phase Advancement</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Alert status="warning" borderRadius="md">
                <AlertIcon as={FiAlertTriangle} />
                <VStack align="start" spacing={1} flex="1">
                  <AlertTitle>Use Only If Automatic Advancement Fails</AlertTitle>
                  <AlertDescription fontSize="sm">
                    This button should only be used if the system does not automatically
                    advance to the next phase when appropriate.
                  </AlertDescription>
                </VStack>
              </Alert>

              <VStack align="start" spacing={2}>
                <Text fontWeight="semibold">What will happen:</Text>
                <Text fontSize="sm">
                  • Phase will advance from <strong>{currentPhase}</strong> to <strong>{nextPhase}</strong>
                </Text>
                <Text fontSize="sm">
                  • This action will be <strong>logged</strong> with full context
                </Text>
                <Text fontSize="sm">
                  • Support team will be <strong>notified via email</strong>
                </Text>
                <Text fontSize="sm">
                  • Incident will appear on <strong>admin dashboard</strong>
                </Text>
              </VStack>

              <Text fontSize="sm" color="gray.600">
                By clicking "Advance Phase", you confirm that the automatic phase transition
                did not occur as expected.
              </Text>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="orange"
              onClick={handleAdvanceClick}
              isLoading={isAdvancing}
              loadingText="Advancing..."
            >
              Advance Phase
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
